/**
 * Public API — no auth required
 *
 * IMPORTANT — Run this SQL in Supabase Dashboard → SQL Editor
 * BEFORE any public submissions will work:
 *
 * -- 1. Allow public support requests
 * CREATE POLICY "Public can submit inquiries"
 *   ON outreach_inquiries FOR INSERT WITH CHECK (true);
 *
 * -- 2. Allow public volunteer applications
 * CREATE POLICY "Public can apply to volunteer"
 *   ON volunteers FOR INSERT WITH CHECK (status = 'applicant');
 *
 * -- 3. Allow public donor contact
 * CREATE POLICY "Public can register as donor"
 *   ON donors FOR INSERT WITH CHECK (true);
 */

import { supabase } from '../supabase'

// All public inquiry submissions use one helper so a successful insert consistently
// triggers a minimal staff email alert. Email failure must never turn a saved request
// into a misleading submission failure for the person asking for help.
const insertPublicInquiry = async (payload: Record<string, unknown>) => {
  // Preserve a caller-supplied ID so the saved row and staff alert always point
  // to the same inquiry. Most callers let this helper generate the ID.
  const inquiryId = typeof payload.id === 'string' ? payload.id : crypto.randomUUID()
  const { error } = await supabase.from('outreach_inquiries').insert({
    ...payload,
    id: inquiryId,
  })

  if (!error) {
    void supabase.functions.invoke('notify-new-inquiry', {
      body: { inquiry_id: inquiryId },
    }).then(({ error: notificationError }) => {
      if (notificationError) {
        console.warn('[W.E.E. public submit] Staff email alert could not be sent:', notificationError.message)
      }
    }).catch((notificationError) => {
      console.warn('[W.E.E. public submit] Staff email alert request failed:', notificationError)
    })
  }

  return { error }
}

// ── Support Request → outreach_inquiries ─────────────────────
export interface SupportRequestPayload {
  first_name:      string
  last_name:       string
  phone:           string
  email?:          string
  county:          string
  household_size?: number
  needs:            string[]
  notes?:          string
  priority?:       string
  consent_followup?: boolean
  insurance_status?: 'insured' | 'uninsured' | 'not_sure' | 'prefer_not_to_say'
  payer_name?: string
  plan_type?: string
  benefits_navigation_requested?: boolean
  insurance_help_areas?: string[]
}

export const submitSupportRequest = async (data: SupportRequestPayload) => {
  // Build a combined notes string that captures all context for staff
  const combinedNotes = [
    `County: ${data.county}`,
    data.household_size ? `Household size: ${data.household_size}` : null,
    data.needs.length  ? `Needs: ${data.needs.map(n => n.replace(/_/g,' ')).join(', ')}` : null,
    data.priority      ? `Priority: ${data.priority}` : null,
    data.notes         ? `Additional: ${data.notes}` : null,
    data.consent_followup ? 'Follow-up consent: Yes' : 'Follow-up consent: No',
  ].filter(Boolean).join('\n')

  const inquiryId = crypto.randomUUID()
  const { error } = await insertPublicInquiry({
    id:          inquiryId,
    first_name:  data.first_name.trim(),
    last_name:   data.last_name.trim(),
    phone:       data.phone.trim(),
    email:       data.email?.trim() || null,
    notes:       combinedNotes,
    status:      'new',
  })

  if (error) {
    console.error('[W.E.E. public submit] outreach_inquiries error:', error)
    throw new Error(error.message)
  }

  const shouldStoreInsuranceAssessment =
    data.insurance_status !== undefined ||
    data.benefits_navigation_requested === true ||
    Boolean(data.payer_name?.trim()) ||
    Boolean(data.plan_type?.trim()) ||
    Boolean(data.insurance_help_areas?.length)

  if (shouldStoreInsuranceAssessment) {
    const { error: insuranceError } = await supabase
      .from('client_insurance_assessments')
      .insert({
        inquiry_id: inquiryId,
        coverage_status: data.insurance_status ?? 'not_sure',
        payer_name: data.payer_name?.trim() || null,
        plan_type: data.plan_type?.trim() || null,
        benefits_navigation_requested: data.benefits_navigation_requested === true,
        assistance_areas: data.insurance_help_areas?.filter(Boolean) ?? [],
      })

    if (insuranceError) {
      console.error('[W.E.E. public submit] insurance assessment error:', insuranceError)
      throw new Error('Your support request was received, but we could not save the insurance/benefits section. Please contact W.E.E. so we can complete that part of your intake.')
    }
  }
}

// ── Volunteer Application → volunteers ────────────────────────
export interface VolunteerApplicationPayload {
  first_name:   string
  last_name:    string
  email:        string
  phone?:       string
  county?:      string   // text name — stored in notes (county_id is UUID FK)
  availability?: string
  interests?:   string[]
  skills?:      string
  notes?:       string
  volunteer_type?: 'individual' | 'group'
  group_name?: string
  group_leader?: string
  group_contact_email?: string
  group_size?: number
  activity_date?: string
  activity_purpose?: string
}

export const submitVolunteerApplication = async (data: VolunteerApplicationPayload) => {
  // Pack county + availability into notes since county_id needs a UUID
  const combinedNotes = [
    data.county      ? `County: ${data.county}` : null,
    data.availability? `Availability: ${data.availability.replace(/_/g,' ')}` : null,
    data.notes       ? data.notes : null,
  ].filter(Boolean).join('\n')

  const skillsArray = data.skills
    ? data.skills.split(',').map(s => s.trim()).filter(Boolean)
    : []

  const { error } = await supabase.from('volunteers').insert({
    first_name:  data.first_name.trim(),
    last_name:   data.last_name.trim(),
    email:       data.email.trim().toLowerCase(),
    phone:       data.phone?.trim() || null,
    county_id:   null,          // UUID FK — set by staff after review
    skills:      skillsArray,
    interests:   data.interests?.map(s => s.trim()).filter(Boolean) ?? [],
    availability: data.availability ? { raw: data.availability } : null,
    notes:       combinedNotes || null,
    registration_type: data.volunteer_type ?? 'individual',
    group_name: data.group_name?.trim() || null,
    group_leader: data.group_leader?.trim() || null,
    group_contact_email: data.group_contact_email?.trim().toLowerCase() || null,
    group_size: data.group_size ?? null,
    activity_date: data.activity_date || null,
    activity_purpose: data.activity_purpose?.trim() || null,
    application_date: new Date().toISOString().slice(0, 10),
    application_source: 'public_guided_access',
    status:      'applicant',
    total_hours: 0,
    state:       'FL',
  })

  if (error) {
    console.error('[W.E.E. public submit] volunteers error:', error)
    throw new Error(error.message)
  }
}

// ── Donor Contact → outreach_inquiries ───────────────────────
// Routed here because the donors table has NOT NULL constraints
// (donor_number, tax_exempt) that require staff review to set correctly.
// Staff creates the official donor record after reviewing the inquiry.
export interface DonorContactPayload {
  first_name?:        string
  last_name?:         string
  organization_name?: string
  email:              string
  phone?:             string
  donor_type:         string
  newsletter_opt_in?: boolean
  notes?:             string
}

export const submitDonorContact = async (data: DonorContactPayload) => {
  const display = (
    data.organization_name?.trim() ||
    `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim() ||
    data.email
  )

  const combinedNotes = [
    `Inquiry type: Donor Contact`,
    `Donor type: ${data.donor_type}`,
    data.organization_name ? `Organization: ${data.organization_name.trim()}` : null,
    `Newsletter opt-in: ${data.newsletter_opt_in ? 'Yes' : 'No'}`,
    data.notes?.trim() ? `Message: ${data.notes.trim()}` : null,
  ].filter(Boolean).join('\n')

  const { error } = await insertPublicInquiry({
    first_name:  (data.first_name?.trim() || display.split(' ')[0] || 'Donor'),
    last_name:   (data.last_name?.trim()  || display.split(' ').slice(1).join(' ') || ''),
    phone:       data.phone?.trim()  || null,
    email:       data.email.trim().toLowerCase(),
    notes:       combinedNotes,
    status:      'new',
  })

  if (error) {
    console.error('[W.E.E. public submit] donor contact error:', error)
    throw new Error(error.message)
  }
}

// ── Partner Inquiry → outreach_inquiries ─────────────────────
export interface PartnerInquiryPayload {
  organization_name: string
  contact_name:      string
  email:             string
  phone?:            string
  agency_type?:      string
  county?:           string
  notes?:            string
}

export const submitPartnerInquiry = async (data: PartnerInquiryPayload) => {
  const nameParts = data.contact_name.trim().split(' ')
  const firstName = nameParts[0] ?? data.contact_name
  const lastName  = nameParts.slice(1).join(' ') || ''

  const combinedNotes = [
    `Organization: ${data.organization_name}`,
    data.agency_type ? `Type: ${data.agency_type}` : null,
    data.county      ? `County: ${data.county}` : null,
    data.notes       ? `Notes: ${data.notes}` : null,
  ].filter(Boolean).join('\n')

  const { error } = await insertPublicInquiry({
    first_name:  firstName,
    last_name:   lastName || '',
    email:       data.email.trim().toLowerCase(),
    phone:       data.phone?.trim() || null,
    notes:       combinedNotes,
    status:      'new',
  })

  if (error) {
    console.error('[W.E.E. public submit] partner inquiry error:', error)
    throw new Error(error.message)
  }
}

// ── Transportation Instability Application ────────────────────
export interface TransportationPayload {
  first_name:      string
  last_name:       string
  phone:           string
  email?:          string
  county:          string
  address?:        string
  reason:          string
  frequency?:      string
  household_size?: number
  notes?:          string
}

export const submitTransportationApplication = async (data: TransportationPayload) => {
  const combinedNotes = [
    'TRANSPORTATION INSTABILITY APPLICATION',
    `County: ${data.county}`,
    data.address        ? `Address: ${data.address}` : null,
    `Reason for need: ${data.reason}`,
    data.frequency      ? `Frequency needed: ${data.frequency}` : null,
    data.household_size ? `Household size: ${data.household_size}` : null,
    data.notes          ? `Additional: ${data.notes}` : null,
  ].filter(Boolean).join('\n')

  const { error } = await insertPublicInquiry({
    first_name: data.first_name.trim(),
    last_name:  data.last_name.trim(),
    phone:      data.phone.trim(),
    email:      data.email?.trim() || null,
    notes:      combinedNotes,
    status:     'new',
  })
  if (error) {
    console.error('[W.E.E. public submit] transportation error:', error)
    throw new Error(error.message)
  }
}

// ── Agency Client Referral ────────────────────────────────────
export interface AgencyReferralPayload {
  // Agency info
  agency_name:     string
  agency_contact:  string
  agency_email?:   string
  agency_phone?:   string
  // Client info
  client_first:    string
  client_last:     string
  client_phone?:   string
  client_county:   string
  // Referral info
  services_needed: string[]
  urgency:         string
  estimated_families?: number
  projected_diaper_packs?: number
  projected_wipes_packs?: number
  projected_formula_cans?: number
  projected_food_boxes?: number
  projected_hygiene_kits?: number
  projected_clothing_sets?: number
  notes?:          string
}

export const submitAgencyReferral = async (data: AgencyReferralPayload) => {
  const combinedNotes = [
    'PARTNER AGENCY ACCESS REQUEST',
    `Referring agency: ${data.agency_name}`,
    `Agency contact: ${data.agency_contact}`,
    data.agency_email ? `Agency email: ${data.agency_email}` : null,
    data.agency_phone ? `Agency phone: ${data.agency_phone}` : null,
    `Client county: ${data.client_county}`,
    `Services needed: ${data.services_needed.join(', ')}`,
    data.estimated_families !== undefined && data.estimated_families > 0 ? `Estimated families / households: ${data.estimated_families}` : null,
    data.projected_diaper_packs !== undefined && data.projected_diaper_packs > 0 ? `Projected diapers (packs/cases): ${data.projected_diaper_packs}` : null,
    data.projected_wipes_packs !== undefined && data.projected_wipes_packs > 0 ? `Projected wipes (packs/cases): ${data.projected_wipes_packs}` : null,
    data.projected_formula_cans !== undefined && data.projected_formula_cans > 0 ? `Projected formula cans: ${data.projected_formula_cans}` : null,
    data.projected_food_boxes !== undefined && data.projected_food_boxes > 0 ? `Projected food boxes / kits: ${data.projected_food_boxes}` : null,
    data.projected_hygiene_kits !== undefined && data.projected_hygiene_kits > 0 ? `Projected hygiene / essential kits: ${data.projected_hygiene_kits}` : null,
    data.projected_clothing_sets !== undefined && data.projected_clothing_sets > 0 ? `Projected clothing sets: ${data.projected_clothing_sets}` : null,
    `Urgency: ${data.urgency}`,
    data.notes ? `Notes: ${data.notes}` : null,
  ].filter(Boolean).join('\n')

  const { error } = await insertPublicInquiry({
    first_name: data.client_first.trim(),
    last_name:  data.client_last.trim(),
    phone:      data.client_phone?.trim() || null,
    // The agency's contact information is preserved in notes above.
    // Do not place the referring agency email in the client's email field;
    // that field may become the client's contact record if the inquiry is converted.
    email:      null,
    notes:      combinedNotes,
    status:     'new',
  })
  if (error) {
    console.error('[W.E.E. public submit] referral error:', error)
    throw new Error(error.message)
  }
}


// ── Resource Listing Request → outreach_inquiries ─────────────
// Public submissions are requests for review only. Staff approves and
// publishes the organization/resource to resource_directory separately.
export interface ResourceListingRequestPayload {
  organization_name: string
  contact_name: string
  email: string
  phone?: string
  website?: string
  service_category?: string
  service_area?: string
  description: string
  notes?: string
}

export const submitResourceListingRequest = async (data: ResourceListingRequestPayload) => {
  const nameParts = data.contact_name.trim().split(' ')
  const combinedNotes = [
    'COMMUNITY RESOURCE LISTING REQUEST',
    `Organization: ${data.organization_name.trim()}`,
    `Contact: ${data.contact_name.trim()}`,
    data.phone ? `Phone: ${data.phone.trim()}` : null,
    data.website ? `Website: ${data.website.trim()}` : null,
    data.service_category ? `Service category: ${data.service_category}` : null,
    data.service_area ? `Service area: ${data.service_area.trim()}` : null,
    `Description: ${data.description.trim()}`,
    data.notes?.trim() ? `Additional notes: ${data.notes.trim()}` : null,
    'Status: Pending W.E.E. review before directory publication.',
  ].filter(Boolean).join('\n')

  const { error } = await insertPublicInquiry({
    first_name: nameParts[0] ?? data.organization_name.trim(),
    last_name: nameParts.slice(1).join(' ') || '',
    email: data.email.trim().toLowerCase(),
    phone: data.phone?.trim() || null,
    notes: combinedNotes,
    status: 'new',
  })

  if (error) {
    console.error('[W.E.E. public submit] resource listing request error:', error)
    throw new Error(error.message)
  }
}

// ── Community Promotion Inquiry → outreach_inquiries ──────────
export interface CommunityPromotionInquiryPayload {
  organization_name: string
  contact_name: string
  email: string
  phone?: string
  website?: string
  promotion_type?: string
  message?: string
}

export const submitCommunityPromotionInquiry = async (data: CommunityPromotionInquiryPayload) => {
  const nameParts = data.contact_name.trim().split(' ')
  const combinedNotes = [
    'COMMUNITY PROMOTION / SPONSORSHIP INQUIRY',
    `Organization / business: ${data.organization_name.trim()}`,
    `Contact: ${data.contact_name.trim()}`,
    data.phone ? `Phone: ${data.phone.trim()}` : null,
    data.website ? `Website: ${data.website.trim()}` : null,
    data.promotion_type ? `Promotion interest: ${data.promotion_type}` : null,
    data.message?.trim() ? `Message: ${data.message.trim()}` : null,
    'Request requires W.E.E. review and confirmation of availability, placement, pricing, and terms.',
  ].filter(Boolean).join('\n')

  const { error } = await insertPublicInquiry({
    first_name: nameParts[0] ?? data.organization_name.trim(),
    last_name: nameParts.slice(1).join(' ') || '',
    email: data.email.trim().toLowerCase(),
    phone: data.phone?.trim() || null,
    notes: combinedNotes,
    status: 'new',
  })

  if (error) {
    console.error('[W.E.E. public submit] community promotion inquiry error:', error)
    throw new Error(error.message)
  }
}


// ── Agency / Doula Crisis Resource Pickup → outreach_inquiries ──
// Records agency contact and requested inventory in the existing inquiry queue.
// This does not create a client/household or promise inventory fulfillment.
export interface AgencyPickupRequestPayload {
  organization_name: string
  organization_type: string
  contact_name: string
  email: string
  phone: string
  county: string
  estimated_families?: number
  requested_items: Array<{ name: string; quantity: number }>
  diaper_sizes?: string
  notes?: string
}

export const submitAgencyPickupRequest = async (data: AgencyPickupRequestPayload) => {
  const nameParts = data.contact_name.trim().split(/\s+/)
  const combinedNotes = [
    'AGENCY & DOULA CRISIS RESOURCE PICKUP REQUEST',
    'Pickup event: Thursday, October 29, 2026, 1:00 PM–4:00 PM',
    'Request status: Submitted for W.E.E. review; inventory and pickup are not yet confirmed.',
    `Organization: ${data.organization_name.trim()}`,
    `Organization type: ${data.organization_type}`,
    `Contact person: ${data.contact_name.trim()}`,
    `County served: ${data.county.trim()}`,
    data.estimated_families !== undefined ? `Approximate families served: ${data.estimated_families}` : null,
    'Resources requested:',
    ...data.requested_items.map(item => `- ${item.name}: ${item.quantity}`),
    data.diaper_sizes?.trim() ? `Diaper sizes / specifications: ${data.diaper_sizes.trim()}` : null,
    data.notes?.trim() ? `Preparation notes: ${data.notes.trim()}` : null,
    'Intake type: agency pickup (not an individual family referral).',
  ].filter(Boolean).join('\n')

  const { error } = await insertPublicInquiry({
    first_name: nameParts[0] || data.organization_name.trim(),
    last_name: nameParts.slice(1).join(' ') || '',
    email: data.email.trim().toLowerCase(),
    phone: data.phone.trim(),
    notes: combinedNotes,
    status: 'new',
  })

  if (error) {
    console.error('[W.E.E. public submit] agency pickup request error:', error)
    throw new Error(error.message)
  }
}
